import type {
  GuaranteedServiceOrgSkuEntry,
  GuaranteedServiceSkuEntry,
  TargetProbeLegRecord,
} from '@nadoutong/contracts';
import { skuNamespaceKey } from '../../components/SkuGroupPicker.tsx';
import { legMatchesQuery, type LegSkuArming } from './legModel.ts';
import {
  foldPhysicalLegs,
  legStateIsAbnormal,
  legStateSeverity,
  type LegState,
  type PhysicalLeg,
} from './legPhysical.ts';
import {
  effectiveRoutings,
  legRoutingRoles,
  legSection,
  routingTakeovers,
  sectionIsLive,
  type EffectiveRouting,
  type LegRoutingRole,
  type LegSection,
  type RoutingTakeovers,
} from './legRouting.ts';
import { overlayFallback } from './modes.ts';
import { LEG_SKU_ALL_OFF, legSkuArming } from './skuArming.ts';

/** One physical leg placed in its SKU: routing roles and 首发 / 兜底 / 不接流量 section. */
export interface PlacedLeg {
  readonly leg: PhysicalLeg;
  readonly roles: readonly LegRoutingRole[];
  readonly section: LegSection;
}

export interface LegSku {
  readonly skuId: string;
  readonly skuName: string;
  readonly row: GuaranteedServiceSkuEntry | null;
  readonly overlays: readonly GuaranteedServiceOrgSkuEntry[];
  /** Has a SKU row or at least one customer overlay in 重点保障. */
  readonly configured: boolean;
  readonly arming: LegSkuArming;
  readonly routings: readonly EffectiveRouting[];
  /** 首发, then 兜底, then 不接流量; heavier first inside a section. */
  readonly legs: readonly PlacedLeg[];
  /** Some leg that takes traffic is red / red outline / yellow. */
  readonly abnormal: boolean;
  /** Worst severity among legs that take traffic, manual excluded. */
  readonly worst: number;
  /**
   * First-traffic legs removed by an armed mechanism (solid red before the
   * rail divider): the SKU is serving from fewer legs than it is configured
   * for, the most dangerous state the rail can show.
   */
  readonly firstRemoved: number;
  /**
   * (routing, region) pairs where every weight > 0 leg is gone and a
   * weight 0 leg now takes every first pick. Counts as the same danger.
   */
  readonly takeovers: number;
  /** Rail danger: a removed first-traffic leg, or a routing running on its fallback. */
  readonly danger: boolean;
}

const SECTION_ORDER: Readonly<Record<LegSection, number>> = { first: 0, reserve: 1, manual: 2, overridden: 3, dead: 4 };

function headlineWeight(placed: PlacedLeg): number {
  return placed.roles.reduce((max, role) => Math.max(max, role.share.weight ?? 0), 0);
}

function sortPlaced(legs: readonly PlacedLeg[]): readonly PlacedLeg[] {
  return [...legs].sort((left, right) =>
    SECTION_ORDER[left.section] - SECTION_ORDER[right.section]
    || headlineWeight(right) - headlineWeight(left)
    || left.leg.head.upstream_name.localeCompare(right.leg.head.upstream_name)
    || left.leg.head.upstream_channel_id.localeCompare(right.leg.head.upstream_channel_id)
    || left.leg.head.physical_model.localeCompare(right.leg.head.physical_model));
}

export interface PlacedLegs {
  readonly routings: readonly EffectiveRouting[];
  readonly legs: readonly PlacedLeg[];
  readonly takeovers: RoutingTakeovers;
}

export function placeLegs(legs: readonly PhysicalLeg[]): PlacedLegs {
  const routings = effectiveRoutings(legs.map((leg) => ({ key: leg.key, members: leg.head.members })));
  const regions = [...new Set(legs.flatMap((leg) => leg.slices.map((entry) => entry.region)))];
  const byKey = new Map(legs.map((leg) => [leg.key, leg]));
  // An armed trip retires the leg from the first pick in that region only.
  const retiredIn = (legKey: string, region: string): boolean =>
    byKey.get(legKey)?.slices.some((entry) => entry.region === region && entry.state === 'tripped') ?? false;
  const takeovers: RoutingTakeovers = new Map(routings.map((routing) => [
    routing.key,
    routingTakeovers(routing, regions, retiredIn),
  ]));
  const placed = legs.map((leg) => {
    const roles = legRoutingRoles(leg.key, routings, takeovers);
    return { leg, roles, section: legSection(leg.reachable, roles) };
  });
  return { routings, legs: sortPlaced(placed), takeovers };
}

function takeoverCount(takeovers: RoutingTakeovers): number {
  let count = 0;
  for (const byLeg of takeovers.values()) {
    count += new Set([...byLeg.values()].flat()).size;
  }
  return count;
}

/**
 * Configured SKUs (a SKU row or a customer overlay) list every leg. Any
 * other SKU lists only the legs an operator removed by hand, so they stay
 * visible (and probe-able) instead of vanishing from the one place that
 * lists legs; a SKU with none is not listed.
 */
export function buildLegSkus(
  slices: readonly TargetProbeLegRecord[],
  rows: readonly GuaranteedServiceSkuEntry[],
  overlays: readonly GuaranteedServiceOrgSkuEntry[],
  nowMs: number,
): readonly LegSku[] {
  const rowBySku = new Map(rows.map((row) => [row.sku_id, row]));
  const overlaysBySku = new Map<string, GuaranteedServiceOrgSkuEntry[]>();
  for (const overlay of overlays) overlaysBySku.set(overlay.sku_id, [...(overlaysBySku.get(overlay.sku_id) ?? []), overlay]);
  const armingOf = (skuId: string): LegSkuArming => {
    const row = rowBySku.get(skuId);
    return row === undefined ? LEG_SKU_ALL_OFF : legSkuArming(row);
  };
  const physical = foldPhysicalLegs(slices, armingOf, nowMs);
  const bySku = new Map<string, { name: string; legs: PhysicalLeg[] }>();
  for (const leg of physical) {
    const entry = bySku.get(leg.head.sku_id) ?? { name: leg.head.sku_name, legs: [] };
    entry.legs.push(leg);
    bySku.set(leg.head.sku_id, entry);
  }
  for (const row of rows) if (!bySku.has(row.sku_id)) bySku.set(row.sku_id, { name: row.sku_name, legs: [] });
  for (const overlay of overlays) {
    if (!bySku.has(overlay.sku_id)) bySku.set(overlay.sku_id, { name: overlay.sku_name, legs: [] });
  }
  const out: LegSku[] = [];
  for (const [skuId, entry] of bySku) {
    const row = rowBySku.get(skuId) ?? null;
    const skuOverlays = overlaysBySku.get(skuId) ?? [];
    const configured = row !== null || skuOverlays.length > 0;
    const shown = configured ? entry.legs : entry.legs.filter((leg) => leg.state === 'manual');
    if (!configured && shown.length === 0) continue;
    const { routings, legs, takeovers } = placeLegs(shown);
    const live = legs.filter((placed) => sectionIsLive(placed.section));
    const firstRemoved = legs.filter((placed) => placed.section === 'first' && placed.leg.state === 'tripped').length;
    const takeoverPairs = takeoverCount(takeovers);
    out.push({
      skuId,
      skuName: entry.name,
      row,
      overlays: skuOverlays,
      configured,
      arming: armingOf(skuId),
      routings,
      legs,
      abnormal: live.some((placed) => legStateIsAbnormal(placed.leg.state)),
      firstRemoved,
      takeovers: takeoverPairs,
      danger: firstRemoved > 0 || takeoverPairs > 0,
      worst: live.reduce((max, placed) =>
        (placed.leg.state === 'manual' ? max : Math.max(max, legStateSeverity(placed.leg.state))), 0),
    });
  }
  return out;
}

export interface LegStateCounts {
  readonly red: number;
  readonly record: number;
  readonly yellow: number;
  readonly green: number;
  readonly immune: number;
  readonly unprobed: number;
  readonly grey: number;
}

export function countLegStates(states: readonly LegState[]): LegStateCounts {
  const count = (wanted: readonly LegState[]): number => states.filter((state) => wanted.includes(state)).length;
  return {
    red: count(['tripped']),
    record: count(['record']),
    yellow: count(['shadow', 'failing']),
    green: count(['ok']),
    immune: count(['immune']),
    unprobed: count(['unprobed']),
    grey: count(['manual']),
  };
}

/** First-traffic legs removed by an armed mechanism, summed over SKUs. */
export function firstRemovedCount(skus: readonly LegSku[]): number {
  return skus.reduce((sum, sku) => sum + sku.firstRemoved, 0);
}

/** States of the legs that take traffic (首发 + 兜底). */
export function liveStates(skus: readonly LegSku[]): readonly LegState[] {
  return skus.flatMap((sku) => sku.legs.filter((placed) => sectionIsLive(placed.section)).map((placed) => placed.leg.state));
}

/** Legs an operator took out by hand (their own section, not live). */
export function manualLegCount(skus: readonly LegSku[]): number {
  return skus.reduce((sum, sku) => sum + sku.legs.filter((placed) => placed.section === 'manual').length, 0);
}

export function skuMatchesQuery(sku: LegSku, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return true;
  if (sku.skuName.toLowerCase().includes(q)) return true;
  return sku.legs.some((placed) => legMatchesQuery(placed.leg.head, q));
}

export interface LegSkuNamespace {
  /** '' for SKU names without a namespace. */
  readonly namespace: string;
  readonly skus: readonly LegSku[];
}

export function groupSkusByNamespace(skus: readonly LegSku[]): readonly LegSkuNamespace[] {
  const byNamespace = new Map<string, LegSku[]>();
  for (const sku of skus) {
    const namespace = skuNamespaceKey(sku.skuName);
    byNamespace.set(namespace, [...(byNamespace.get(namespace) ?? []), sku]);
  }
  return [...byNamespace.keys()]
    .sort((left, right) => {
      if (left === '') return 1;
      if (right === '') return -1;
      return left.localeCompare(right);
    })
    .map((namespace) => ({
      namespace,
      skus: [...(byNamespace.get(namespace) ?? [])].sort((left, right) =>
        Number(right.danger) - Number(left.danger)
        || left.skuName.localeCompare(right.skuName)),
    }));
}

/** Rail order: namespaces, then SKUs with a removed first-traffic leg, then SKU names. */
export function railOrder(skus: readonly LegSku[]): readonly LegSku[] {
  return groupSkusByNamespace(skus).flatMap((group) => group.skus);
}

/**
 * First selection: the worst abnormal SKU in rail order (a removed
 * first-traffic leg first, then red before yellow), else the first shown
 * configured SKU that has legs, else anything listed.
 */
export function defaultSelectedSku(shown: readonly LegSku[], all: readonly LegSku[]): string | null {
  const configured = railOrder(shown.filter((sku) => sku.configured));
  const abnormal = configured.filter((sku) => sku.abnormal);
  const worst = [...abnormal].sort((left, right) =>
    Number(right.danger) - Number(left.danger) || right.worst - left.worst)[0];
  const withLegs = (skus: readonly LegSku[]): readonly LegSku[] => [
    ...skus.filter((sku) => sku.legs.length > 0),
    ...skus.filter((sku) => sku.legs.length === 0),
  ];
  return worst?.skuId
    ?? withLegs(configured)[0]?.skuId
    ?? withLegs(railOrder(all.filter((sku) => sku.configured)))[0]?.skuId
    ?? railOrder(all)[0]?.skuId
    ?? null;
}

/**
 * Customer overlays whose ultimate fallback is in effect (`act`). Reads the
 * resolved mode; a row without `effective` (older ops API) falls back to
 * its own setting.
 */
export function overlayFallbackOnCount(sku: LegSku): number {
  return sku.overlays.filter((overlay) => {
    const effective: { readonly ultimate_fallback_mode?: unknown } | undefined = overlay.effective;
    const mode = effective?.ultimate_fallback_mode ?? overlayFallback(overlay);
    return mode === 'act';
  }).length;
}
